import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import { useToast } from '../../hooks/useToast';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ActivateIcon,
  Cancel as DeactivateIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  AccessTime as TimeIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { ConfirmModal } from '../../components/ui/Modal';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { Toast } from '../../components/ui/Toast';
import { Breadcrumbs } from '../../components/ui/Breadcrumbs';

interface User {
  _id: string;
  email: string;
  name: string;
  roles: string[];
  activeRole: string;
  isAdmin: boolean;
  createdAt: string;
  lastLogin: string | null;
  actif: boolean;
}

const UsersList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Toast notifications
  const { toasts, removeToast, success, error: showError } = useToast();

  // Search & Filters
  const [search, setSearch] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    severity: 'info' | 'warning' | 'error' | 'success';
  }>({ open: false, title: '', message: '', onConfirm: () => {}, severity: 'warning' });

  // Filtrer les utilisateurs
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(search.toLowerCase()) ||
                         user.email.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.roles.includes(roleFilter);
    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && user.actif) ||
                         (statusFilter === 'inactive' && !user.actif);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Paginer les résultats
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl('/api/users'), {
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des utilisateurs');
      }

      const data = await response.json();
      setUsers(data);
      success('Utilisateurs chargés avec succès');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const showDeleteConfirm = (userId: string, userEmail: string) => {
    setConfirmModal({
      open: true,
      title: 'Supprimer l\'utilisateur',
      message: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${userEmail} ? Cette action est irréversible.`,
      onConfirm: () => handleDelete(userId),
      severity: 'error'
    });
  };

  const showDeactivateConfirm = (userId: string, userEmail: string) => {
    setConfirmModal({
      open: true,
      title: 'Désactiver l\'utilisateur',
      message: `Êtes-vous sûr de vouloir désactiver l'utilisateur ${userEmail} ?`,
      onConfirm: () => handleDeactivate(userId),
      severity: 'warning'
    });
  };

  const handleDelete = async (userId: string) => {
    setConfirmModal({ ...confirmModal, open: false });
    try {
      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/users/${userId}`), {
        method: 'DELETE',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de l\'utilisateur');
      }

      success('Utilisateur supprimé avec succès');
      loadUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      showError(errorMessage);
    }
  };

  const handleActivate = async (userId: string, userEmail: string) => {
    try {
      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/users/${userId}/activate`), {
        method: 'PUT',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de l\'activation de l\'utilisateur');
      }

      success(`L'utilisateur ${userEmail} a été activé`);
      loadUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de l\'activation';
      showError(errorMessage);
    }
  };

  const handleDeactivate = async (userId: string) => {
    setConfirmModal({ ...confirmModal, open: false });
    const userEmail = users.find(u => u._id === userId)?.email || '';

    try {
      const token = localStorage.getItem('@TikodAdmin:token');
      const response = await fetch(buildApiUrl(`/api/users/${userId}/deactivate`), {
        method: 'PUT',
        headers: {
          'x-auth-token': token || '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erreur lors de la désactivation de l\'utilisateur');
      }

      success(`L'utilisateur ${userEmail} a été désactivé`);
      loadUsers();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la désactivation';
      showError(errorMessage);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'provider':
        return 'primary';
      case 'partner':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Composant carte utilisateur pour mobile
  const UserCard: React.FC<{ user: User }> = ({ user }) => (
    <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {user.name}
          </Typography>
          <Chip
            label={user.actif ? 'Actif' : 'Inactif'}
            size="small"
            color={user.actif ? 'success' : 'default'}
            variant={user.actif ? 'filled' : 'outlined'}
          />
        </Box>

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Rôles:
            </Typography>
            {user.roles.map((role) => (
              <Chip
                key={role}
                label={role}
                size="small"
                color={getRoleColor(role) as any}
                variant="outlined"
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
            <Typography variant="body2" color="text.secondary">
              Admin: {user.isAdmin ? 'Oui' : 'Non'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TimeIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />
            <Typography variant="body2" color="text.secondary">
              Dernière connexion: {user.lastLogin
                ? new Date(user.lastLogin).toLocaleDateString('fr-FR')
                : 'Jamais'
              }
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'space-between', px: 2 }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/users/${user._id}`)}
          color="primary"
        >
          Modifier
        </Button>

        <Box>
          {user.actif ? (
            <IconButton
              size="small"
              onClick={() => showDeactivateConfirm(user._id, user.email)}
              color="warning"
              title="Désactiver"
            >
              <DeactivateIcon />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={() => handleActivate(user._id, user.email)}
              color="success"
              title="Activer"
            >
              <ActivateIcon />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => showDeleteConfirm(user._id, user.email)}
            color="error"
            title="Supprimer"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </CardActions>
    </Card>
  );

  // Handle pagination changes
  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }]} />
        <Paper sx={{ p: 3 }}>
          <TableSkeleton rows={5} />
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: 'Utilisateurs', path: '/users' }]} />

      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
          Gestion des Utilisateurs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={Link}
          to="/users/new"
          fullWidth={isMobile}
          sx={{
            background: 'linear-gradient(135deg, #129baf 0%, #0e7989 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e7989 0%, #096670 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(18, 155, 175, 0.4)',
            },
          }}
        >
          Nouvel utilisateur
        </Button>
      </Box>

      {/* Search & Filters */}
      <Paper sx={{ p: { xs: 2, sm: 2 }, mb: 3 }}>
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          alignItems: 'center',
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' }
        }}>
          <TextField
            label="Rechercher"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            size="small"
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          />

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Rôle</InputLabel>
            <Select
              value={roleFilter}
              label="Rôle"
              onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous les rôles</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="provider">Fournisseur</MenuItem>
              <MenuItem value="partner">Partenaire</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              <MenuItem value="active">Actifs</MenuItem>
              <MenuItem value="inactive">Inactifs</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={loadUsers}
            color="primary"
            title="Actualiser"
            sx={{ ml: 'auto' }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {filteredUsers.length} utilisateur{filteredUsers.length > 1 ? 's' : ''} trouvé{filteredUsers.length > 1 ? 's' : ''}
        {search && ` pour "${search}"`}
      </Typography>

      {/* Vue mobile - Cartes */}
      {isMobile ? (
        <Box>
          {paginatedUsers.map((user) => (
            <UserCard key={user._id} user={user} />
          ))}
          {paginatedUsers.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {search || roleFilter !== 'all' || statusFilter !== 'all'
                  ? 'Aucun utilisateur ne correspond aux filtres'
                  : 'Aucun utilisateur trouvé'}
              </Typography>
            </Paper>
          )}
          {filteredUsers.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 2 }}
            />
          )}
        </Box>
      ) : (
        // Vue desktop - Tableau
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rôles</TableCell>
                <TableCell>Rôle actif</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Dernière connexion</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                <TableRow key={user._id} hover>
                  <TableCell component="th" scope="row">
                    {user.name}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.roles.map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          color={getRoleColor(role) as any}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.activeRole}
                      size="small"
                      color={getRoleColor(user.activeRole) as any}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isAdmin ? 'Oui' : 'Non'}
                      size="small"
                      color={user.isAdmin ? 'error' : 'default'}
                      variant={user.isAdmin ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.actif ? 'Actif' : 'Inactif'}
                      size="small"
                      color={user.actif ? 'success' : 'default'}
                      variant={user.actif ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleDateString('fr-FR')
                      : 'Jamais'
                    }
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/users/${user._id}`)}
                      color="primary"
                      title="Modifier"
                    >
                      <EditIcon />
                    </IconButton>
                    {user.actif ? (
                      <IconButton
                        size="small"
                        onClick={() => showDeactivateConfirm(user._id, user.email)}
                        color="warning"
                        title="Désactiver"
                      >
                        <DeactivateIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleActivate(user._id, user.email)}
                        color="success"
                        title="Activer"
                      >
                        <ActivateIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => showDeleteConfirm(user._id, user.email)}
                      color="error"
                      title="Supprimer"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {search || roleFilter !== 'all' || statusFilter !== 'all'
                        ? 'Aucun utilisateur ne correspond aux filtres'
                        : 'Aucun utilisateur trouvé'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {filteredUsers.length > rowsPerPage && (
            <TablePagination
              component="div"
              count={filteredUsers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </TableContainer>
      )}

      {/* Toast notifications */}
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          open={true}
          onClose={() => removeToast(toast.id)}
          message={toast.message}
          severity={toast.severity}
        />
      ))}

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        severity={confirmModal.severity}
        confirmText="Confirmer"
        cancelText="Annuler"
      />
    </Container>
  );
};

export default UsersList; 